import axios from 'axios'
import env from '../../env'
import axiosRetry from 'axios-retry'

const useAxios = () => {
  const axiosClient = axios.create({
    baseURL:
      env.REACT_APP_ENVIRONMENT === 'local'
        ? `http://${window.location.hostname}:8002/api/`
        : `https://${env.REACT_APP_API_URL}/api/`,
    timeout: 1000000,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Frontend-Domain': window.location.hostname,
    },
  })

  // Flag to prevent multiple refresh token requests
  let isRefreshing = false
  let failedQueue = []

  const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error)
      } else {
        prom.resolve(token)
      }
    })
    failedQueue = []
  }

  axiosClient.interceptors.request.use(
    (config) => {
      const accessToken = localStorage.getItem('access_token')
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  axiosClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config

      // If there's no refresh token, reject immediately
      const refreshToken = localStorage.getItem('refresh_token')
      if (!refreshToken || refreshToken === 'null') {
        return Promise.reject(error)
      }

      // Handle 401 errors
      if (error?.response?.status === 401 && !originalRequest._retry) {
        // If it's a refresh token request that failed, clear tokens and reject
        if (originalRequest.url === 'main/token/refresh/') {
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          return Promise.reject(error)
        }

        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then((token) => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`
              return axiosClient(originalRequest)
            })
            .catch((err) => Promise.reject(err))
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const tokenParts = JSON.parse(atob(refreshToken.split('.')[1]))
          const now = Math.ceil(Date.now() / 1000)

          if (tokenParts.exp <= now) {
            throw new Error('Refresh token expired')
          }

          const response = await axiosClient.post('/main/token/refresh/', {
            refresh: refreshToken,
          })

          if (response?.data?.access) {
            const newAccessToken = response.data.access
            localStorage.setItem('access_token', newAccessToken)
            if (response.data.refresh) {
              localStorage.setItem('refresh_token', response.data.refresh)
            }

            axiosClient.defaults.headers[
              'Authorization'
            ] = `Bearer ${newAccessToken}`
            originalRequest.headers[
              'Authorization'
            ] = `Bearer ${newAccessToken}`

            processQueue(null, newAccessToken)
            return axiosClient(originalRequest)
          }
        } catch (refreshError) {
          processQueue(refreshError, null)
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }

      return Promise.reject(error)
    }
  )

  // Configure axios-retry
  axiosRetry(axiosClient, {
    retries: 3,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: (error) => {
      // Don't retry on 401/403 errors
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return false
      }
      // Retry on network errors and 5xx responses
      return axiosRetry.isNetworkOrIdempotentRequestError(error)
    },
  })

  return axiosClient
}

export default useAxios
